import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import SGDLogo from 'assets/currencies/sgd.svg'
import BOC_logo from 'assets/methods/boc.jpg'
import DBS_logo from 'assets/methods/dbs.jpg'
import HSBC_logo from 'assets/methods/hsbc.jpg'
import MayBank_logo from 'assets/methods/maybank.jpg'
import OCBC_logo from 'assets/methods/ocbc.jpg'
import SCB_logo from 'assets/methods/scb.jpg'
import UOB_logo from 'assets/methods/uob.jpg'
import PayQRCode from 'assets/payment_qr.jpg'
import Card from 'components/Card'
import TopUpLayout from 'components/Layouts/TopUpLayout'
import Loader from 'components/Loader'
import useAuth from 'hooks/useAuth'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const PaymentMethod = () => {
  const navigate = useNavigate()
  const auth = useAuth()
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>(undefined)
  const [isVerifying, setIsVerifying] = useState(false)

  const payMethods = [
    { name: 'UOB', imgSrc: UOB_logo },
    { name: 'OCBC', imgSrc: OCBC_logo },
    { name: 'DBS', imgSrc: DBS_logo },
    { name: 'SCB', imgSrc: SCB_logo },
    { name: 'MayBank', imgSrc: MayBank_logo },
    { name: 'BOC', imgSrc: BOC_logo },
    { name: 'HSBC', imgSrc: HSBC_logo },
  ]

  const handleBackClick = () => {
    if (selectedMethod) {
      setSelectedMethod(undefined)
    } else {
      navigate('/topup/enter-amount')
    }
  }

  const handleVerifyClick = async () => {
    setIsVerifying(true)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    auth.performTopUp()
    setIsVerifying(false)
    navigate('/topup/cash-in')
  }

  return (
    <TopUpLayout>
      <Card>
        <div className="p-6 border-b border-neutral-200">
          <h2 className="text-xl font-semibold text-neutral-800">
            {selectedMethod ? 'Complete Payment' : 'Select Bank'}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            {selectedMethod
              ? 'Scan the QR code with your banking app'
              : 'Choose your bank to link your account'}
          </p>
        </div>

        <div className="p-6">
          {selectedMethod ? (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex-1">
                  <div className="bg-neutral-50 rounded-xl p-4 mb-4">
                    <span className="text-xs text-neutral-500 uppercase tracking-wide block mb-1">
                      Amount to pay
                    </span>
                    <div className="flex items-center gap-2">
                      <img
                        className="w-6 h-6 rounded-full shadow-sm"
                        src={SGDLogo}
                        alt="SGD"
                      />
                      <span className="text-2xl font-bold text-neutral-800">
                        {auth.topUpAmount()} SGD
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600">
                    Open your <strong>{selectedMethod}</strong> mobile app and scan this QR code to complete the payment.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-white rounded-xl border border-neutral-200 p-2 inline-block">
                    <img
                      src={PayQRCode}
                      alt="Payment QR Code"
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                </div>
              </div>

              <button
                onClick={handleVerifyClick}
                className="btn-primary btn-lg w-full"
                disabled={isVerifying}
              >
                {isVerifying ? <Loader /> : 'I have completed payment'}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {payMethods.map((method, index) => (
                <button
                  key={index}
                  className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-neutral-50 transition-colors text-left group"
                  onClick={() => setSelectedMethod(method.name)}
                >
                  <div className="w-10 h-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center overflow-hidden">
                    <img
                      className="w-6 h-6 object-contain"
                      src={method.imgSrc}
                      alt={method.name}
                    />
                  </div>
                  <span className="flex-1 font-medium text-neutral-800">
                    {method.name}
                  </span>
                  <ChevronRightIcon className="w-5 h-5 text-neutral-400 group-hover:text-neutral-600 transition-colors" />
                </button>
              ))}
            </div>
          )}

          <button
            onClick={handleBackClick}
            className="mt-6 flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
        </div>
      </Card>
    </TopUpLayout>
  )
}

export default PaymentMethod
